/**
 * Unified Login — the one and only public entry point.
 * Two modes:
 *   • Password  — email + password, calls /api/v1/auth/login
 *   • Email OTP — email → request OTP → enter 6-digit code → calls /api/v1/auth/verify-otp
 *
 * On success, auto-routes by tenant.code:
 *   TRACK0TIC → /control/dashboard
 *   any other → /ops/dashboard
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, KeyRound, Mail } from 'lucide-react';
import { TrackOticBrand } from '@/components/Brand';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function PortalPickerPage() {
  const [mode, setMode] = useState('password'); // 'password' | 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [devOtp, setDevOtp] = useState(null); // visible in dev/mock mode only
  const [loading, setLoading] = useState(false);
  const { login, applySessionUser } = useTrackOticAuth();
  const navigate = useNavigate();

  const routeAfterLogin = (tenant) => {
    if (tenant?.code === 'TRACK0TIC') {
      toast.success('Welcome to Track0tic Control Portal');
      navigate('/admin/dashboard', { replace: true });
    } else {
      toast.success(`Welcome to ${tenant?.name || 'Customer Portal'}`);
      navigate('/ops/dashboard', { replace: true });
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { tenant } = await login(email, password);
      routeAfterLogin(tenant);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      setLoading(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Enter your email first');
      return;
    }
    setLoading(true);
    try {
      // Phase 7: explicit channel = email so the backend sends via SendGrid.
      const res = await axios.post(`${API_BASE}/api/v1/auth/request-otp`, { email, channel: 'email' });
      setOtpSent(true);
      if (res.data?.dev_otp) {
        setDevOtp(res.data.dev_otp);
        toast.success(`OTP sent (DEV/MOCK: ${res.data.dev_otp})`);
      } else {
        toast.success('OTP sent. Check your email.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast.error('Enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/api/v1/auth/verify-otp`, { email, otp });
      // Cookie-only (Phase 7): the HttpOnly session cookie is set by the response — no access_token in body.
      const { user, tenant } = res.data;
      await applySessionUser(user, tenant);
      routeAfterLogin(tenant);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OTP verification failed');
      setLoading(false);
    }
  };

  const resetOtp = () => {
    setOtpSent(false);
    setOtp('');
    setDevOtp(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex flex-col items-center">
            <TrackOticBrand size="4xl" showTagline className="text-white" />
          </div>
        </div>

        {/* Login card */}
        <div className="bg-slate-900 rounded-xl p-8 border border-slate-800">
          {/* Mode tabs */}
          <div className="flex bg-slate-800 rounded-lg p-1 mb-6" data-testid="login-mode-tabs">
            <button
              type="button"
              onClick={() => { setMode('password'); resetOtp(); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === 'password' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="mode-password"
            >
              <KeyRound className="w-4 h-4" /> Password
            </button>
            <button
              type="button"
              onClick={() => { setMode('otp'); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === 'otp' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="mode-otp"
            >
              <Mail className="w-4 h-4" /> Email OTP
            </button>
          </div>

          {/* Password form */}
          {mode === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-5" data-testid="unified-login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                  autoFocus
                  data-testid="login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                  data-testid="login-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading
                  ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</>)
                  : 'Sign In'}
              </Button>
            </form>
          )}

          {/* OTP form */}
          {mode === 'otp' && (
            <form onSubmit={otpSent ? handleVerifyOtp : handleRequestOtp} className="space-y-5" data-testid="otp-login-form">
              <div className="space-y-2">
                <Label htmlFor="otp-email" className="text-slate-300">Email</Label>
                <Input
                  id="otp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  disabled={otpSent}
                  className="bg-slate-800 border-slate-700 text-white disabled:opacity-60"
                  required
                  autoFocus
                  data-testid="otp-email"
                />
              </div>

              {otpSent && (
                <div className="space-y-2">
                  <Label htmlFor="otp-code" className="text-slate-300">6-digit OTP</Label>
                  <Input
                    id="otp-code"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    className="bg-slate-800 border-slate-700 text-white tracking-widest text-center text-xl"
                    required
                    data-testid="otp-code"
                  />
                  {devOtp && (
                    <p className="text-amber-400 text-xs">
                      DEV/MOCK mode: use OTP <strong className="font-mono">{devOtp}</strong>
                    </p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
                data-testid="otp-submit"
              >
                {loading
                  ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{otpSent ? 'Verifying...' : 'Sending OTP...'}</>)
                  : (otpSent ? 'Verify & Sign In' : 'Send OTP')}
              </Button>

              {otpSent && (
                <button
                  type="button"
                  onClick={resetOtp}
                  className="text-slate-500 hover:text-blue-400 text-xs w-full text-center"
                  data-testid="otp-change-email"
                >
                  Use a different email
                </button>
              )}
            </form>
          )}
        </div>

        <p className="text-center text-slate-600 text-xs mt-8">
          © Track0tic{'\u2122'} · track verified
        </p>
      </div>
    </div>
  );
}
